import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

interface FieldShellProps {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
}

function FieldShell({ label, htmlFor, hint, error, required, children }: FieldShellProps) {
  return (
    <div className={['field', error && 'field-error'].filter(Boolean).join(' ')}>
      <label htmlFor={htmlFor} className="field-label">
        {label}
        {required && <span className="field-required" aria-hidden="true"> *</span>}
      </label>
      {children}
      {error ? (
        <div className="field-msg field-msg-error" role="alert">{error}</div>
      ) : hint ? (
        <div className="field-msg">{hint}</div>
      ) : null}
    </div>
  );
}

interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
}

export function TextInput({ label, hint, error, id, required, ...rest }: TextInputProps) {
  const inputId = id ?? `f-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <FieldShell label={label} htmlFor={inputId} hint={hint} error={error} required={required}>
      <input id={inputId} className="field-input" required={required} {...rest} />
    </FieldShell>
  );
}

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: string;
  error?: string;
}

export function TextArea({ label, hint, error, id, required, ...rest }: TextAreaProps) {
  const inputId = id ?? `f-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <FieldShell label={label} htmlFor={inputId} hint={hint} error={error} required={required}>
      <textarea id={inputId} className="field-textarea" required={required} {...rest} />
    </FieldShell>
  );
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  hint?: string;
  error?: string;
  options: { value: string; label: string }[];
}

export function Select({ label, hint, error, id, required, options, ...rest }: SelectProps) {
  const inputId = id ?? `f-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <FieldShell label={label} htmlFor={inputId} hint={hint} error={error} required={required}>
      <select id={inputId} className="field-input" required={required} {...rest}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}
